import React, { FC, useCallback, useEffect, useState, useContext } from 'react';
import { TasksContext, TasksDispatchContext, TasksProvider, useTask } from '../stateManagement/context';
import {
    ComponentTypeID,
    IAdequacy,
    IControlMeasure, IControlMeasureAssessment, IControlMeasureCost,
    IControlMeasureFailureMode,
    IControlType, IFailureModeAssessment, IHarm,
    IProjectFailureMode, IRiskAssessment
} from "../types/types";

import {
    Checkbox,
    FormControl,
    makeStyles,
    MenuItem,
    Select,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@material-ui/core";
import FailureModesControlMeasureSelect from "./failure_mode_control_measure";
import ControlTypeSelection from "./control_type";
import AdequacySelection from "./adequacy_selection";
import {CostAdequacy, order_of_mag} from "./control_measure_cost";
import Comments from "./comments";
import { APPLICATION } from '../env';
import { max } from 'date-fns';

import { Button } from "@material-ui/core";
import JustificationDialog from "./JustificationDialog";



interface IReasonablyPracticableTestProps {
    control_measure: IControlMeasure
    control_measure_cost: IControlMeasureCost
    comment_id: number
    control_types: IControlType[]
    adequacies: IAdequacy[]
    failure_mode_assessments: IFailureModeAssessment[]
    control_measure_assessments: IControlMeasureAssessment[]
    updateControlMeasures: (controlMeasureAssessment: IControlMeasureAssessment) => void;
    project_failure_modes: IProjectFailureMode[]
    risk_assessments: IRiskAssessment[]
    is_failure_mode_centric: boolean
}



const useStyles = makeStyles((theme) => ({
    button: {
        display: 'block',
        marginTop: theme.spacing(2),
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120,
        maxWidth: 200,
        whiteSpace: "normal",
        fontSize: 14,
    },
    menu:{
        width: 200,
        whiteSpace: "normal",
        fontSize: 14,
    },
    listItem: {
        whiteSpace: "normal",
        fontSize: 14,


    }
}));

const ReasonablyPracticableSummary: FC<IReasonablyPracticableTestProps> = ({control_measure,
                                                                                    control_types,
                                                                                    comment_id,
                                                                                    failure_mode_assessments,
                                                                                    control_measure_assessments,
                                                                                    control_measure_cost,
                                                                                    adequacies,
                                                                                    risk_assessments,
                                                                                    updateControlMeasures,
                                                                                    project_failure_modes,
                                                                                    is_failure_mode_centric,
                                                                                }) => {

    const classes = useStyles();

    // filter risk assessment in project failure modes

    // extract project_failure_mode_ids

    let pm_ids = project_failure_modes.map( pm => pm.id)

    let f_risk_assessments = risk_assessments.filter( ra => pm_ids.includes(ra.project_failure_mode_id))

    // sort by max_societal_risk
    f_risk_assessments.sort( (a, b) => (a.societal_risk || 0) - (b.societal_risk || 0))
    let pf_index = 0
    let max_project_failure_mode_id = 0
    if (f_risk_assessments.length > 0) {
            
        max_project_failure_mode_id = f_risk_assessments[0].project_failure_mode_id || 0

        pf_index = project_failure_modes.findIndex( pm => pm.id === max_project_failure_mode_id)

        if (pf_index == -1) {
            pf_index = 0
        }
    }
                                                     
    const [justification, setJustification] = useState<string>(control_measure_assessments[pf_index].justification as string);
    const [deMinimis, setDeMinimis] = useState<boolean>(control_measure_assessments[pf_index].de_minimis as boolean);
    const [goodPractice, setGoodPractice] = useState<boolean>(control_measure_assessments[pf_index].good_practice as boolean);
    const [practicable, setPracticable] = useState<boolean>(control_measure_assessments[pf_index].practicable as boolean);
    const [effective, setEffective] = useState<boolean>(control_measure_assessments[pf_index].effective as boolean);
    const [considered, setConsidered] = useState<boolean>(control_measure_assessments[pf_index].considered as boolean);
    const [overallAssessmentID, setOverallAssessmentID] = useState<number>(control_measure_assessments.find( (pf) => pf.project_failure_mode_id === max_project_failure_mode_id)?.overall_assessment_id as number);
    const [overallJustification, setOverallJustification] = useState<string>(control_measure_assessments.find( (pf) => pf.project_failure_mode_id === max_project_failure_mode_id || (is_failure_mode_centric && pf.project_failure_mode_id==-1))?.overall_justification || "");
    const [consideredFailureModes] = useState<IFailureModeAssessment[]>(failure_mode_assessments.filter( (pf) => pf.consider === true));
    let overallAssessment = (failure_mode_assessments.find( (pf) => pf.project_failure_mode_id === max_project_failure_mode_id|| (is_failure_mode_centric && pf.project_failure_mode_id==-1))?.assessment || "");
    const [justificationOpen, setJustificationOpen] = useState(false);

    const previewText = (text: string, maxLen = 180) => {
    if (!text) return "";
    const t = text.trim();
    return t.length > maxLen ? t.slice(0, maxLen) + "…" : t;
    };

    const failureModePreview = previewText(overallAssessment || "", 180);
    const overallPreview = previewText(overallJustification || "", 180);
    const hasAnyJustification = !!(overallAssessment || overallJustification);
    let dispatch = useContext(TasksDispatchContext)
    let tasks = useTask()


    // if length is 0, then return <> </>
    if (f_risk_assessments.length == 0) {
        return <></>
    }

    // This is to disable overall assessment if control centric
    if (project_failure_modes.filter( 
        (pf) => 
            consideredFailureModes.filter( (pfa) =>
                pfa.project_failure_mode_id === pf.id || (pfa.project_failure_mode_id === -1 && is_failure_mode_centric)
            ).length > 0
    ).length > 0 && is_failure_mode_centric) {
        overallAssessment = "";
    }

    return (
        <TableRow>
            <TableCell>{control_measure.description}</TableCell>
            <TableCell>{control_types.find( ct => ct.id === control_measure.control_type_id)?.name}</TableCell>

            <TableCell>
                <Checkbox
                    checked={goodPractice}
                    disabled={true}
                    color="primary"
                />
            </TableCell>
            <TableCell
                style={{whiteSpace: "nowrap"}}>
                {order_of_mag(control_measure_cost)}
            </TableCell>
            <TableCell>
                {CostAdequacy(control_measure_cost)}
            </TableCell>
            <TableCell>
                <Checkbox
                    checked={deMinimis}
                    disabled={true}
                    color="primary"
                />
            </TableCell>
            <TableCell>                
                {

                    project_failure_modes.filter( 
                        (pf) => 
                            consideredFailureModes.filter( (pfa) =>
                                pfa.project_failure_mode_id === pf.id || (pfa.project_failure_mode_id === -1 && is_failure_mode_centric)
                            ).length > 0
                    ).map( (pf, index) =>
                    {
                        // check if failure mode is inculded in the control measure

                        return <><Typography><small><b>{index == 0 ? APPLICATION == "ALARP" ?  "Priority Failure Modes": "Critical Failure Modes" : ""}</b></small></Typography>{index >0 ? " ,":""}{pf.failure_mode?.name}{pf.id ==max_project_failure_mode_id?"*": "" }</>
                    })
                }
                {
                    project_failure_modes.filter( 
                        (pf) => 
                            consideredFailureModes.filter( (pfa) =>
                                pfa.project_failure_mode_id === pf.id  || (pfa.project_failure_mode_id === -1 && is_failure_mode_centric)
                            ).length == 0
                    ).map( (pf, index) =>
                    {
                        // check if failure mode is inculded in the control measure
                        return <><Typography><small><b>{index == 0 ? "Additional Failure Modes":""}</b></small></Typography>{index >0 ? " ,":""}{pf.failure_mode?.name}</>
                    })
                }
                
            </TableCell>

<TableCell>
  {/* Concise preview */}
  {overallAssessment && (
    <>
      <Typography style={{ fontSize: 12, fontWeight: 700 }}>
        Failure Mode wide Justification
      </Typography>
      <Typography style={{ fontSize: 13, color: "#444" }}>
        {failureModePreview}
      </Typography>
    </>
  )}

  <Typography style={{ fontSize: 12, fontWeight: 700, marginTop: overallAssessment ? 8 : 0 }}>
    Control Measure Justification
  </Typography>

  <Typography style={{ fontSize: 13, color: "#444" }}>
    {overallPreview || "(No justification provided)"}
  </Typography>

  {/* Action */}
  {hasAnyJustification && (
    <Button
      size="small"
      color="primary"
      style={{ paddingLeft: 0, marginTop: 6, textTransform: "none" }}
      onClick={() => setJustificationOpen(true)}
    >
      View full justification
    </Button>
  )}

  {/* Dialog */}
  <JustificationDialog
    open={justificationOpen}
    onClose={() => setJustificationOpen(false)}
    title={`Justification — ${control_measure.description}`}
    failureModeJustification={overallAssessment || ""}
    controlMeasureJustification={overallJustification || ""}
  />
</TableCell>


            <TableCell>
                {control_measure.uuid && <Comments parent_uuid={parseInt(""+ comment_id + control_measure.uuid)} type_id={ComponentTypeID.SummaryReasonablyPracticable}/>}
            </TableCell>



        </TableRow>
    );
}

export default ReasonablyPracticableSummary;
